import { findBeginnerDevice } from '@skyrover/blockly-blocks';

import { arduinoGenerator } from './arduino-generator.js';

/** Shared I2S clock/data pins for MAX98357 + INMP441 on the same bus. */
export const DEFAULT_I2S_AUDIO_PINS = {
  bclk: 20,
  ws: 8,
  din: 19,
  sd: 15,
} as const;

export interface I2sAudioPins {
  bclk: number;
  ws: number;
  din: number;
  sd: number;
}

export function resolveI2sAudioPins(): I2sAudioPins {
  const board = arduinoGenerator.activeBoard;
  const speaker = findBeginnerDevice(board, 'speaker-1')?.mapping ?? {};
  const mic = findBeginnerDevice(board, 'microphone-1')?.mapping ?? {};

  const bclk = Number(
    speaker.bclk ?? speaker.sck ?? mic.sck ?? mic.bclk ?? DEFAULT_I2S_AUDIO_PINS.bclk,
  );
  const ws = Number(speaker.ws ?? speaker.lrclk ?? mic.ws ?? mic.lrclk ?? DEFAULT_I2S_AUDIO_PINS.ws);
  const din = Number(speaker.din ?? speaker.dout ?? DEFAULT_I2S_AUDIO_PINS.din);
  const sd = Number(mic.sd ?? mic.dout ?? DEFAULT_I2S_AUDIO_PINS.sd);

  return {
    bclk: Number.isFinite(bclk) ? bclk : DEFAULT_I2S_AUDIO_PINS.bclk,
    ws: Number.isFinite(ws) ? ws : DEFAULT_I2S_AUDIO_PINS.ws,
    din: Number.isFinite(din) ? din : DEFAULT_I2S_AUDIO_PINS.din,
    sd: Number.isFinite(sd) ? sd : DEFAULT_I2S_AUDIO_PINS.sd,
  };
}

function i2sAudioBusHelperCode(pins: I2sAudioPins): string {
  return [
    `// Shared I2S bus — MAX98357 (BCLK=${pins.bclk}, DIN=${pins.din}, WS=${pins.ws})`,
    `//                INMP441  (SCK=${pins.bclk}, SD=${pins.sd}, WS=${pins.ws})`,
    `#define SKYROVER_I2S_BCLK ${pins.bclk}`,
    `#define SKYROVER_I2S_WS ${pins.ws}`,
    `#define SKYROVER_I2S_DIN ${pins.din}`,
    `#define SKYROVER_I2S_SD ${pins.sd}`,
    '#define SKYROVER_I2S_PORT I2S_NUM_0',
    '#define SKYROVER_I2S_SAMPLE_RATE 16000',
    '',
    'bool skyroverI2sAudioReady = false;',
    '',
    'void skyroverI2sAudioBegin() {',
    '  if (skyroverI2sAudioReady) {',
    '    return;',
    '  }',
    '  const i2s_config_t skyroverI2sConfig = {',
    '    .mode = (i2s_mode_t)(I2S_MODE_MASTER | I2S_MODE_TX | I2S_MODE_RX),',
    '    .sample_rate = SKYROVER_I2S_SAMPLE_RATE,',
    '    .bits_per_sample = I2S_BITS_PER_SAMPLE_16BIT,',
    '    .channel_format = I2S_CHANNEL_FMT_ONLY_LEFT,',
    '    .communication_format = I2S_COMM_FORMAT_STAND_I2S,',
    '    .intr_alloc_flags = ESP_INTR_FLAG_LEVEL1,',
    '    .dma_buf_count = 8,',
    '    .dma_buf_len = 256,',
    '    .use_apll = false,',
    '    .tx_desc_auto_clear = true,',
    '    .fixed_mclk = 0',
    '  };',
    '  const i2s_pin_config_t skyroverI2sPins = {',
    '    .bck_io_num = SKYROVER_I2S_BCLK,',
    '    .ws_io_num = SKYROVER_I2S_WS,',
    '    .data_out_num = SKYROVER_I2S_DIN,',
    '    .data_in_num = SKYROVER_I2S_SD',
    '  };',
    '  i2s_driver_install(SKYROVER_I2S_PORT, &skyroverI2sConfig, 0, NULL);',
    '  i2s_set_pin(SKYROVER_I2S_PORT, &skyroverI2sPins);',
    '  i2s_zero_dma_buffer(SKYROVER_I2S_PORT);',
    '  skyroverI2sAudioReady = true;',
    '}',
  ].join('\n');
}

/** Install shared duplex I2S once for speaker TX + microphone RX. */
export function ensureI2sAudioBus(): void {
  const pins = resolveI2sAudioPins();
  arduinoGenerator.addInclude('#include <math.h>');
  arduinoGenerator.addInclude('#include <driver/i2s.h>');
  arduinoGenerator.addGlobal(i2sAudioBusHelperCode(pins));
  arduinoGenerator.addSetupOnce('beginner-i2s-audio-bus', 'skyroverI2sAudioBegin();');
}
